import React, { useState, useCallback, useRef } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useDispatch, useSelector } from 'react-redux';
import {
  uploadBackground,
  trackUserEvent,
  showModal,
} from '@shoflo/studio-redux/actions';
import {
  IMAGE_FILE,
  formatBytes,
  validateBackgroundUpload,
} from '@shoflo/polymer';
import {
  HiddenInputFile,
  StyledButton,
  UploadZone,
} from './BackgroundUploadForm.styled';
import { func } from 'prop-types';
import { UPLOAD_ERROR_FORM } from 'constants/Modal';
import {
  UPLOAD_ERROR_TITLE,
  UPLOAD_ERROR_DESCRIPTION,
} from 'constants/BackgroundUploadConstants';
import { useBackgroundUploadedOnStageSLI } from '@studio/metrics';
function BackgroundUploadForm({ callback }) {
  const dispatch = useDispatch();
  const inputFile = useRef(null);
  const uploadZone = useRef(null);
  const [disabled, setDisabled] = useState(false);
  const [isFileDragOver, setIsFileDragOver] = useState(false);

  const {
    startBackgroundUploadedOnStageSLI,
    endBackgroundUploadedOnStageSLIIncomplete,
  } = useBackgroundUploadedOnStageSLI();
  const { user, trackableUserEvent } = useSelector((state) => state);

  const handleFileChange = useCallback(
    async (event) => {
      let errorFiles = [];
      const files = Array.from(event);
      if (files.length > 1) {
        datadogRum.addError(UPLOAD_ERROR_TITLE, {
          error: 'You can only upload one file at a time',
        });
        dispatch(
          showModal(UPLOAD_ERROR_FORM, {
            title: UPLOAD_ERROR_TITLE,
            description: UPLOAD_ERROR_DESCRIPTION,
            files: [
              {
                errors: ['You can only upload one file at a time'],
              },
            ],
          })
        );
        return;
      }
      function handleUploadError(file, errors) {
        errorFiles.push({
          name: file.name,
          size: formatBytes(file.size),
          errors,
        });
      }

      async function handleFileUpload(file, fileCount) {
        setDisabled(true);
        const errors = await validateBackgroundUpload(file, fileCount);
        if (errors && errors.length > 0) {
          handleUploadError(file, errors);
        } else {
          const layer = {
            title: file.name,
            type: IMAGE_FILE,
            file,
          };
          // If a background upload was in-progress, mark the previous one as incomplete_measure so that next SLI can begin properly
          endBackgroundUploadedOnStageSLIIncomplete(
            trackableUserEvent?.userId,
            trackableUserEvent?.name,
            user?._id
          );

          dispatch(
            uploadBackground(layer, initiateSliBackgroundUploadedOnStage)
          );
          callback();
        }
      }

      await Promise.all(
        files.map((file) => handleFileUpload(file, files.length))
      );

      if (errorFiles.length > 0) {
        datadogRum.addError(UPLOAD_ERROR_TITLE, errorFiles);
        dispatch(
          showModal(UPLOAD_ERROR_FORM, {
            title: UPLOAD_ERROR_TITLE,
            description: UPLOAD_ERROR_DESCRIPTION,
            files: errorFiles,
          })
        );
      }
    },
    [callback, dispatch]
  );

  function handleButtonClick() {
    inputFile.current.click();
    uploadZone.current.focus();
  }

  function handleDragOver(event) {
    event.preventDefault();
    setIsFileDragOver(true);
  }

  function handleDrop(event) {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.files;
    if (!droppedFiles.length) {
      setIsFileDragOver(false);
    } else {
      handleFileChange(droppedFiles);
    }
  }

  function initiateSliBackgroundUploadedOnStage(backgroundId) {
    startBackgroundUploadedOnStageSLI(
      user._id,
      trackUserEvent,
      dispatch,
      backgroundId
    );
  }

  return (
    <UploadZone
      ref={uploadZone}
      data-testid="backgroundUploadZone"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragEnter={() => setIsFileDragOver(true)}
      onDragLeave={() => setIsFileDragOver(false)}
      isFileDragOver={isFileDragOver}
      tabIndex={0}
    >
      <>
        <p>
          Drag file here to upload your own image. Jpg, jpeg, png, gif (5 MB
          limit).
          <br />
          Images can be no larger than 1920x1080 px.
        </p>
        <HiddenInputFile
          type="file"
          id="backgroundFileFile"
          aria-labelledby="backgroundFileLabel"
          accept=".png, .bmp, .jpeg, .jpg, .gif"
          onChange={(e) => handleFileChange(e.target.files)}
          name="file"
          data-testid="backgroundFileInput"
          ref={inputFile}
        />
        <StyledButton
          disabled={disabled || isFileDragOver}
          onClick={handleButtonClick}
          data-testid="backgroundButtonInput"
        >
          Open File Browser
        </StyledButton>
      </>
    </UploadZone>
  );
}

BackgroundUploadForm.propTypes = {
  callback: func,
};

export default BackgroundUploadForm;
